import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import type { WorkflowDefinition } from '@corner/shared';

export type WorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped';

export type WorkflowRunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'canceled';

export interface WorkflowRunStep {
  nodeId: string;
  status: WorkflowStepStatus;
  startedAt: string | null;
  completedAt: string | null;
  output: unknown;
  error: string | null;
}

export interface WorkflowRunSnapshot {
  id: string;
  workflowId: string;
  status: WorkflowRunStatus;
  definition: WorkflowDefinition;
  steps: WorkflowRunStep[];
  collectedVariables: Record<string, unknown>;
  documentIds: string[];
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
  /** Monotonic workflow event sequence; pass as SSE `lastEventSeq` after hydrating from GET. */
  lastEventSeq?: number;
}

export interface UseWorkflowRunStreamOptions {
  runId: string | null;
  getToken: () => string | null;
}

export interface UseWorkflowRunStreamResult {
  run: WorkflowRunSnapshot | null;
  connectionState: 'idle' | 'connecting' | 'open' | 'closed' | 'error';
  cancel: () => Promise<void>;
}

/** Normalize steps embedded in `run.completed` / GET payloads (dates may be ISO strings). */
function normalizeRunStepsFromPayload(raw: unknown): WorkflowRunStep[] | null {
  if (!Array.isArray(raw)) return null;
  const out: WorkflowRunStep[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const nodeId = typeof o.nodeId === 'string' ? o.nodeId : null;
    if (!nodeId) continue;
    const status = o.status as WorkflowRunStep['status'];
    const started =
      o.startedAt == null
        ? null
        : typeof o.startedAt === 'string'
          ? o.startedAt
          : null;
    const completed =
      o.completedAt == null
        ? null
        : typeof o.completedAt === 'string'
          ? o.completedAt
          : null;
    out.push({
      nodeId,
      status: status ?? 'pending',
      startedAt: started,
      completedAt: completed,
      output: o.output ?? null,
      error: o.error != null ? String(o.error) : null,
    });
  }
  return out.length ? out : null;
}

export function useWorkflowRunStream({ runId, getToken }: UseWorkflowRunStreamOptions): UseWorkflowRunStreamResult {
  const [run, setRun] = useState<WorkflowRunSnapshot | null>(null);
  /** Increment after a successful GET so SSE opens only on a hydrated snapshot (avoids dropping events when prev is null). */
  const [hydrateGeneration, setHydrateGeneration] = useState(0);
  const [connectionState, setConnectionState] = useState<UseWorkflowRunStreamResult['connectionState']>('idle');
  const esRef = useRef<EventSource | null>(null);
  const lastSeqRef = useRef(0);
  const runRef = useRef<WorkflowRunSnapshot | null>(null);
  const recoveryTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const scheduleSnapshotRecoveryRef = useRef<(delayMs?: number) => void>(() => {});
  runRef.current = run;

  const fetchSnapshot = useCallback(async () => {
    if (!runId) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.get(`/api/workflow-runs/${runId}`, { headers: { Authorization: `Bearer ${token}` } });
      const snapshot = res.data.run as WorkflowRunSnapshot;
      setRun(snapshot);
      lastSeqRef.current = snapshot.lastEventSeq ?? 0;
      // #region agent log
      fetch('http://127.0.0.1:7675/ingest/4eb987b4-fa77-42e3-95cf-87b64e13ddeb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'fbf77c' },
        body: JSON.stringify({
          sessionId: 'fbf77c',
          hypothesisId: 'H-wizard-refetch',
          location: 'useWorkflowRunStream.ts:fetchSnapshot',
          message: 'workflow_snapshot_poll',
          data: { runId, status: snapshot.status, stepCount: snapshot.steps?.length ?? 0 },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
    } catch {
      /* best-effort */
    }
  }, [runId, getToken]);

  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  const scheduleSnapshotRecovery = useCallback((delayMs = 400) => {
    if (!runId) return;
    clearRecoveryTimer();
    recoveryTimerRef.current = window.setTimeout(async () => {
      recoveryTimerRef.current = null;
      await fetchSnapshot();
      const current = runRef.current;
      if (current && current.status !== 'completed' && current.status !== 'failed' && current.status !== 'canceled') {
        scheduleSnapshotRecoveryRef.current(1600);
      }
    }, delayMs);
  }, [clearRecoveryTimer, fetchSnapshot, runId]);
  scheduleSnapshotRecoveryRef.current = scheduleSnapshotRecovery;

  // Fetch initial snapshot (must complete before SSE — otherwise applyEvent drops every event while prev is null)
  useEffect(() => {
    let cancelled = false;
    if (!runId) {
      setRun(null);
      setHydrateGeneration(0);
      lastSeqRef.current = 0;
      clearRecoveryTimer();
      return;
    }
    const token = getToken();
    if (!token) return;
    setHydrateGeneration(0);
    axios
      .get(`/api/workflow-runs/${runId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const snapshot = res.data.run as WorkflowRunSnapshot;
        setRun(snapshot);
        lastSeqRef.current = snapshot.lastEventSeq ?? 0;
        setHydrateGeneration((n) => n + 1);
      })
      .catch(() => { /* ignored */ });
    return () => { cancelled = true; };
  }, [runId, getToken, clearRecoveryTimer]);

  // If the tab sleeps mid-run, SSE can stall — reconcile from Mongo when visible again.
  useEffect(() => {
    if (!runId) return;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      const r = runRef.current;
      if (!r || r.status === 'completed' || r.status === 'failed' || r.status === 'canceled') return;
      void fetchSnapshot();
      scheduleSnapshotRecovery(1600);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [runId, fetchSnapshot, scheduleSnapshotRecovery]);

  // SSE subscribe (after snapshot hydrate so prev is never null on first events)
  useEffect(() => {
    if (!runId || hydrateGeneration === 0) return;
    const token = getToken();
    if (!token) return;
    // EventSource can't set headers — pass token as query
    const url = `/api/workflow-runs/${runId}/stream?lastEventSeq=${lastSeqRef.current}&token=${encodeURIComponent(token)}`;
    setConnectionState('connecting');
    const es = new EventSource(url);
    esRef.current = es;
    let errorRefetchTimer: ReturnType<typeof window.setTimeout> | null = null;

    const handle = (ev: MessageEvent) => {
      try {
        const payload = JSON.parse(ev.data) as { seq: number; type: string; payload: unknown; at: string };
        lastSeqRef.current = Math.max(lastSeqRef.current, payload.seq);
        if (payload.type === 'run.completed' || payload.type === 'run.failed' || payload.type === 'run.canceled') {
          clearRecoveryTimer();
        }
        setRun((prev) => {
          const next = applyEvent(prev, payload);
          // #region agent log
          fetch('http://127.0.0.1:7675/ingest/4eb987b4-fa77-42e3-95cf-87b64e13ddeb', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'fbf77c' },
            body: JSON.stringify({
              sessionId: 'fbf77c',
              runId: String(runId),
              hypothesisId: 'H-sse',
              location: 'useWorkflowRunStream.ts:handle',
              message: 'workflow_sse_event',
              data: {
                type: payload.type,
                seq: payload.seq,
                nodeId: (payload.payload as Record<string, unknown>)?.nodeId,
                prevWasNull: prev == null,
              },
              timestamp: Date.now(),
            }),
          }).catch(() => {});
          // #endregion
          return next;
        });
      } catch { /* ignore */ }
    };

    const eventTypes = [
      'run.started', 'run.completed', 'run.failed', 'run.canceled',
      'step.started', 'step.completed', 'step.failed', 'step.skipped',
    ];
    eventTypes.forEach((t) => es.addEventListener(t, handle));

    es.onopen = () => setConnectionState('open');
    es.onerror = () => {
      setConnectionState((s) => (s === 'connecting' || s === 'open' ? 'error' : s));
      // Finite streams call res.end() after the last event — browsers still surface that as an error.
      // Re-fetch authoritative steps from Mongo so the wizard strip cannot stay stuck (e.g. on Sign)
      // after dispatch emails already went out.
      if (errorRefetchTimer) window.clearTimeout(errorRefetchTimer);
      errorRefetchTimer = window.setTimeout(() => {
        scheduleSnapshotRecovery(0);
      }, 150);
      es.close();
    };

    return () => {
      if (errorRefetchTimer) window.clearTimeout(errorRefetchTimer);
      clearRecoveryTimer();
      eventTypes.forEach((t) => es.removeEventListener(t, handle));
      es.close();
      esRef.current = null;
      setConnectionState('closed');
    };
  }, [runId, getToken, hydrateGeneration, clearRecoveryTimer, scheduleSnapshotRecovery]);

  const cancel = useCallback(async () => {
    if (!runId) return;
    const token = getToken();
    await axios.post(`/api/workflow-runs/${runId}/cancel`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => { /* ignore */ });
  }, [runId, getToken]);

  return { run, connectionState, cancel };
}

// Apply a single event to the snapshot state
function applyEvent(prev: WorkflowRunSnapshot | null, ev: { type: string; payload: unknown }): WorkflowRunSnapshot | null {
  if (!prev) return prev;
  const payload = ev.payload as Record<string, unknown>;
  switch (ev.type) {
    case 'run.started':
      return { ...prev, status: 'running' };
    case 'run.completed': {
      const rawSteps = payload.steps;
      const mergedSteps = normalizeRunStepsFromPayload(rawSteps);
      // #region agent log
      fetch('http://127.0.0.1:7675/ingest/4eb987b4-fa77-42e3-95cf-87b64e13ddeb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'fbf77c' },
        body: JSON.stringify({
          sessionId: 'fbf77c',
          hypothesisId: 'H-run-complete-merge',
          location: 'useWorkflowRunStream.ts:applyEvent:run.completed',
          message: 'run_completed_merge',
          data: {
            rawKind:
              rawSteps === undefined ? 'undefined'
              : rawSteps === null ? 'null'
              : Array.isArray(rawSteps) ? 'array'
              : typeof rawSteps,
            rawLen: Array.isArray(rawSteps) ? rawSteps.length : null,
            mergedLen: mergedSteps?.length ?? null,
            mergedWasSkipped: mergedSteps == null,
            prevRunningNodeIds: prev.steps.filter((s) => s.status === 'running').map((s) => s.nodeId),
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      return {
        ...prev,
        status: 'completed',
        collectedVariables: (payload.variables as Record<string, unknown>) ?? prev.collectedVariables,
        ...(mergedSteps ? { steps: mergedSteps } : {}),
      };
    }
    case 'run.failed':
      return { ...prev, status: 'failed', error: (payload.error as string) ?? null };
    case 'run.canceled':
      return { ...prev, status: 'canceled' };
    case 'step.started': {
      const nodeId = payload.nodeId as string;
      const existing = prev.steps.find((s) => s.nodeId === nodeId);
      const next = existing
        ? prev.steps.map((s) => s.nodeId === nodeId ? { ...s, status: 'running' as const } : s)
        : [...prev.steps, { nodeId, status: 'running' as const, startedAt: null, completedAt: null, output: null, error: null }];
      return { ...prev, steps: next };
    }
    case 'step.completed': {
      const nodeId = payload.nodeId as string;
      const output = payload.output as unknown;
      const existing = prev.steps.some((s) => s.nodeId === nodeId);
      const steps = existing
        ? prev.steps.map((s) => s.nodeId === nodeId ? { ...s, status: 'completed' as const, output } : s)
        : [...prev.steps, {
          nodeId,
          status: 'completed' as const,
          startedAt: null,
          completedAt: null,
          output,
          error: null,
        }];
      return { ...prev, steps };
    }
    case 'step.failed': {
      const nodeId = payload.nodeId as string;
      const error = (payload.error as string) ?? null;
      const existing = prev.steps.some((s) => s.nodeId === nodeId);
      const steps = existing
        ? prev.steps.map((s) => s.nodeId === nodeId ? { ...s, status: 'failed' as const, error } : s)
        : [...prev.steps, {
          nodeId,
          status: 'failed' as const,
          startedAt: null,
          completedAt: null,
          output: null,
          error,
        }];
      return { ...prev, steps };
    }
    case 'step.skipped': {
      const nodeId = payload.nodeId as string;
      const existing = prev.steps.find((s) => s.nodeId === nodeId);
      const next = existing
        ? prev.steps.map((s) => s.nodeId === nodeId ? { ...s, status: 'skipped' as const } : s)
        : [...prev.steps, { nodeId, status: 'skipped' as const, startedAt: null, completedAt: null, output: null, error: null }];
      return { ...prev, steps: next };
    }
    default:
      return prev;
  }
}
